import type { Locality, Region } from '@shared/schema';

/**
 * Geography context utilities — shared client-side helpers.
 *
 * Active geography is persisted to localStorage under 'kalovate.geography'.
 * Shape: { regionSlug: string, localitySlug?: string }
 */

export interface PersistedGeography {
  regionSlug: string;
  localitySlug?: string;
}

export interface LocalityOption extends Locality {
  regionName: string;
  regionSlug: string;
}

const STORAGE_KEY = 'kalovate.geography';
export const DEFAULT_REGION_SLUG = 'hoosier-hills';
const GEOGRAPHY_SECTION_SLUGS = new Set(['classes', 'creators', 'calendar', 'community']);

const LEGACY_CITY_GEOGRAPHY: Record<string, PersistedGeography> = {
  bloomington: { regionSlug: 'hoosier-hills', localitySlug: 'bloomington' },
  ellettsville: { regionSlug: 'hoosier-hills', localitySlug: 'ellettsville' },
  online: { regionSlug: 'online' },
};

interface PersistedGeographySnapshot {
  geography: PersistedGeography;
  hasPersistedSelection: boolean;
}

function resolveLegacyGeographySlug(value: string | undefined): PersistedGeography | null {
  if (!value) {
    return null;
  }

  return LEGACY_CITY_GEOGRAPHY[value.toLowerCase()] ?? null;
}

function normalizePersistedGeographyCandidate(candidate: unknown): PersistedGeography | null {
  if (typeof candidate === 'string') {
    const normalizedPath = candidate.replace(/\/+$/, '') || '/';
    const pathMatch =
      normalizedPath.match(/^\/cities\/([^/]+)(?:\/(classes|creators|calendar|community))?$/) ??
      normalizedPath.match(/^\/regions\/([^/]+)(?:\/([^/]+))?$/) ??
      normalizedPath.match(/^\/([^/]+)$/);

    if (!pathMatch) {
      return resolveLegacyGeographySlug(candidate);
    }

    if (normalizedPath.startsWith('/regions/')) {
      const localitySlug =
        pathMatch[2] && !GEOGRAPHY_SECTION_SLUGS.has(pathMatch[2]) ? pathMatch[2] : undefined;

      return {
        regionSlug: pathMatch[1],
        localitySlug,
      };
    }

    return resolveLegacyGeographySlug(pathMatch[1]);
  }

  if (!candidate || typeof candidate !== 'object') {
    return null;
  }

  const persisted = candidate as Partial<PersistedGeography> & {
    citySlug?: unknown;
    pathname?: unknown;
    path?: unknown;
  };

  if (typeof persisted.regionSlug === 'string') {
    const legacyMatch = resolveLegacyGeographySlug(persisted.regionSlug);
    if (legacyMatch && typeof persisted.localitySlug !== 'string') {
      return legacyMatch;
    }

    return {
      regionSlug: persisted.regionSlug,
      localitySlug: typeof persisted.localitySlug === 'string' ? persisted.localitySlug : undefined,
    };
  }

  if (typeof persisted.citySlug === 'string') {
    return resolveLegacyGeographySlug(persisted.citySlug);
  }

  if (typeof persisted.pathname === 'string') {
    return normalizePersistedGeographyCandidate(persisted.pathname);
  }

  if (typeof persisted.path === 'string') {
    return normalizePersistedGeographyCandidate(persisted.path);
  }

  return null;
}

export function readPersistedGeography(): PersistedGeographySnapshot {
  const defaultSnapshot = {
    geography: { regionSlug: DEFAULT_REGION_SLUG },
    hasPersistedSelection: false,
  } satisfies PersistedGeographySnapshot;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return defaultSnapshot;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw) as unknown;
    } catch {
      parsed = raw;
    }

    const normalized = normalizePersistedGeographyCandidate(parsed);
    if (normalized) {
      if (JSON.stringify(parsed) !== JSON.stringify(normalized)) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
      }

      return {
        geography: normalized,
        hasPersistedSelection: true,
      };
    }

    localStorage.removeItem(STORAGE_KEY);
    return defaultSnapshot;
  } catch {
    return defaultSnapshot;
  }
}

// ─── Persistence ──────────────────────────────────────────────────────────────

export function getPersistedGeography(): PersistedGeography {
  return readPersistedGeography().geography;
}

export function persistGeography(geo: PersistedGeography): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(geo));
  } catch {
    // localStorage unavailable — silently ignore
  }
}

export function clearPersistedGeography(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage unavailable — silently ignore
  }
}

export async function fetchAllPhysicalLocalities(): Promise<LocalityOption[]> {
  const regionsResponse = await fetch('/api/regions');
  if (!regionsResponse.ok) {
    return [];
  }

  const regions = (await regionsResponse.json()) as Region[];
  const physicalRegions = regions.filter(region => region.type === 'physical');
  const localityGroups = await Promise.all(
    physicalRegions.map(async region => {
      const localitiesResponse = await fetch(`/api/regions/${region.slug}/localities`);
      if (!localitiesResponse.ok) {
        return [] as LocalityOption[];
      }

      const localities = (await localitiesResponse.json()) as Locality[];
      return localities.map(locality => ({
        ...locality,
        regionName: region.name,
        regionSlug: region.slug,
      }));
    })
  );

  return localityGroups.flat();
}

// ─── Timezone formatting ──────────────────────────────────────────────────────

export function formatInTimezone(
  date: Date | string,
  timezone: string,
  options: Intl.DateTimeFormatOptions = {}
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    dateStyle: 'medium',
    timeStyle: 'short',
    ...options,
  };
  try {
    return new Intl.DateTimeFormat('en-US', defaultOptions).format(d);
  } catch {
    return d.toLocaleString();
  }
}

export function formatDateInTimezone(date: Date | string, timezone: string): string {
  // Explicitly unset timeStyle — formatInTimezone defaults both styles, which
  // would append the time to a date-only label.
  return formatInTimezone(date, timezone, { dateStyle: 'medium', timeStyle: undefined });
}

export function formatTimeInTimezone(date: Date | string, timezone: string): string {
  // Explicitly unset dateStyle — see formatDateInTimezone.
  return formatInTimezone(date, timezone, { dateStyle: undefined, timeStyle: 'short' });
}

export function getTimezoneAbbr(date: Date | string, timezone: string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(d);
    return parts.find(p => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

export function formatSessionTime(
  startDate: Date | string,
  endDate: Date | string,
  timezone: string
): string {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;

  const datePart = formatDateInTimezone(start, timezone);
  const startTime = formatTimeInTimezone(start, timezone);
  const endTime = formatTimeInTimezone(end, timezone);
  const abbr = getTimezoneAbbr(start, timezone);

  return `${datePart} · ${startTime} – ${endTime}${abbr ? ` ${abbr}` : ''}`;
}

export function getTimezoneForLocality(
  locality: { timezone: string } | null | undefined,
  region: { timezone: string } | null | undefined
): string {
  return locality?.timezone ?? region?.timezone ?? 'America/Indiana/Indianapolis';
}

export function isOnlineRegion(region: { type: string } | null | undefined): boolean {
  return region?.type === 'online';
}
